import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { Users, RefreshCw, CheckCircle2, Circle } from "lucide-react";

/**
 * Admin dashboard for abandoned-signup leads.
 *
 * Route: /admin/leads
 *
 * Shows anyone who typed their email into the signup form on the landing
 * page but bailed before submitting. `converted` rows are people who
 * eventually came back and finished — those get a green check so we can
 * measure conversion rate on the nudge email that fires from the hourly
 * background sweep.
 */
export default function AdminLeads() {
    const { user } = useAuth();
    const [leads, setLeads] = useState(null);
    const [includeConverted, setIncludeConverted] = useState(true);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const isAdmin =
        user &&
        (user.role === "admin" ||
            user.email === "admin@atlas.app" ||
            user.email === "purposelyy@gmail.com");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get("/admin/leads/abandoned", {
                params: { include_converted: includeConverted, limit: 200 },
            });
            setLeads(data.leads || []);
        } catch (ex) {
            setError(ex.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [includeConverted]);

    useEffect(() => {
        if (isAdmin) load();
    }, [isAdmin, load]);

    if (user === undefined) return null;
    if (!isAdmin) return <Navigate to="/" replace />;

    const totalConverted = (leads || []).filter((l) => l.converted_at).length;
    const totalNudged = (leads || []).filter((l) => l.notified_at).length;
    const conversionRate = leads?.length
        ? Math.round((totalConverted / leads.length) * 100)
        : 0;

    return (
        <div className="min-h-screen w-full bg-slate-50">
            <TopBar />
            <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8" data-testid="admin-leads-page">
                <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-display font-black text-4xl tracking-tighter text-slate-900 inline-flex items-center gap-3">
                            <Users className="w-8 h-8" /> Abandoned signups
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                            Emails typed into the register form on the landing page but never submitted.
                            The Day-1 nudge email fires once (24–72h after first blur) for anyone still uncovered.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-600 inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeConverted}
                                onChange={(e) => setIncludeConverted(e.target.checked)}
                                data-testid="admin-leads-include-converted"
                            />
                            Show converted
                        </label>
                        <button
                            type="button"
                            onClick={load}
                            className="arcade-btn"
                            data-testid="admin-leads-refresh"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                    </div>
                </header>

                {/* Stats */}
                {leads && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-leads-stats">
                        <Stat label="Leads" value={leads.length} />
                        <Stat label="Nudged" value={totalNudged} />
                        <Stat label="Converted" value={totalConverted} />
                        <Stat label="Conv rate" value={`${conversionRate}%`} />
                    </div>
                )}

                {error && (
                    <div className="rounded-2xl bg-sciences/10 border border-sciences/20 text-sciences px-4 py-3 mb-4 text-sm" data-testid="admin-leads-error">
                        {error}
                    </div>
                )}

                {leads && leads.length === 0 && (
                    <div className="crystal rounded-3xl p-10 text-center text-slate-500" data-testid="admin-leads-empty">
                        No abandoned leads yet — quiet is a good sign.
                    </div>
                )}

                {leads && leads.length > 0 && (
                    <div className="crystal rounded-3xl overflow-hidden" data-testid="admin-leads-table">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
                                <tr>
                                    <th className="text-left px-4 py-3">Email</th>
                                    <th className="text-left px-4 py-3">First seen</th>
                                    <th className="text-left px-4 py-3">Blurs</th>
                                    <th className="text-left px-4 py-3">Nudged</th>
                                    <th className="text-left px-4 py-3">Converted</th>
                                    <th className="text-left px-4 py-3">Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((l, i) => (
                                    <tr
                                        key={l.email}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                        data-testid={`admin-lead-row-${i}`}
                                    >
                                        <td className="px-4 py-3 font-mono text-xs text-slate-900">{l.email}</td>
                                        <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                                            {(l.first_seen || "").slice(0, 16).replace("T", " ")}
                                        </td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{l.blur_count || 1}</td>
                                        <td className="px-4 py-3">
                                            {l.notified_at ? (
                                                <span className="text-[11px] chip bg-slate-100 text-slate-700">
                                                    <CheckCircle2 className="w-3 h-3" /> yes
                                                </span>
                                            ) : (
                                                <Circle className="w-3.5 h-3.5 text-slate-300" />
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {l.converted_at ? (
                                                <span className="text-[11px] chip bg-trades/15 text-trades font-bold">
                                                    <CheckCircle2 className="w-3 h-3" /> converted
                                                </span>
                                            ) : (
                                                <Circle className="w-3.5 h-3.5 text-slate-300" />
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 text-xs">{l.source || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="crystal rounded-2xl px-4 py-3" data-testid={`admin-leads-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                {label}
            </div>
            <div className="font-display font-black text-2xl text-slate-900 leading-none mt-1">
                {value}
            </div>
        </div>
    );
}
